import { cn } from "@/lib/cn";

export type StatusTone = "default" | "success" | "warning" | "danger" | "info";

const TONE_CLASS: Record<StatusTone, string> = {
  default: "",
  success: "is-success",
  warning: "is-warning",
  danger: "is-danger",
  info: "is-info",
};

export interface StatusLabelProps {
  tone?: StatusTone;
  className?: string;
  children: React.ReactNode;
}

/** 原型基元：`.status-label`。 */
export function StatusLabel({ tone = "default", className, children }: StatusLabelProps) {
  return <span className={cn("status-label", TONE_CLASS[tone], className)}>{children}</span>;
}

const VALIDATION_TONE: Record<string, StatusTone> = {
  pass: "success",
  warning: "warning",
  danger: "danger",
  info: "info",
};

const VALIDATION_LABEL: Record<string, string> = {
  pass: "通过",
  warning: "需确认",
  danger: "需修复",
  info: "进行中",
};

/** 结构检查规则状态 → 原型状态标签（`app.js` statusLabel 的等价实现）。 */
export function RuleStatus({ status }: { status: string }) {
  return (
    <StatusLabel tone={VALIDATION_TONE[status] ?? "default"}>
      {VALIDATION_LABEL[status] ?? status}
    </StatusLabel>
  );
}

const GENERATION_TONE: Record<string, StatusTone> = {
  approved: "success",
  review: "warning",
  running: "info",
  queued: "default",
  failed: "danger",
};

const GENERATION_LABEL: Record<string, string> = {
  approved: "已通过",
  review: "待审核",
  running: "生成中",
  queued: "等待中",
  failed: "失败",
};

/** 生成任务状态 → 原型状态标签（`app.js` generationStatus 的等价实现）。 */
export function GenerationStatus({ status }: { status: string }) {
  return (
    <StatusLabel tone={GENERATION_TONE[status] ?? "default"}>
      {GENERATION_LABEL[status] ?? status}
    </StatusLabel>
  );
}

const ARTIFACT_TONE: Record<string, StatusTone> = {
  approved: "success",
  submitted: "info",
  draft: "default",
  rejected: "danger",
  stale: "warning",
  superseded: "default",
};

const ARTIFACT_LABEL: Record<string, string> = {
  approved: "已批准",
  submitted: "待审阅",
  draft: "草稿",
  rejected: "已驳回",
  stale: "已失效",
  superseded: "已被取代",
};

/**
 * Artifact 生命周期状态 → 原型状态标签（V102/V103）。
 *
 * 取值必须覆盖后端 `STATUS_TRANSITIONS` 的全部键；未识别状态**原样呈现**，
 * 不要用 `?? "草稿"` 之类兜底把它伪装成一个合法状态。
 */
export function ArtifactStatus({ status }: { status: string }) {
  return (
    <StatusLabel tone={ARTIFACT_TONE[status] ?? "default"}>
      {ARTIFACT_LABEL[status] ?? status}
    </StatusLabel>
  );
}

const RUN_TONE: Record<string, StatusTone> = {
  QUEUED: "default",
  RUNNING: "info",
  VALIDATING: "info",
  WAITING_FOR_REVIEW: "warning",
  APPROVED: "success",
  REJECTED: "danger",
  RETRYABLE_FAILED: "danger",
  BLOCKED: "danger",
  CANCELLED: "default",
};

const RUN_LABEL: Record<string, string> = {
  QUEUED: "等待中",
  RUNNING: "生成中",
  VALIDATING: "校验中",
  WAITING_FOR_REVIEW: "待审核",
  APPROVED: "已通过",
  REJECTED: "已驳回",
  RETRYABLE_FAILED: "可重试失败",
  BLOCKED: "已阻塞",
  CANCELLED: "已取消",
};

/**
 * 能力运行状态 → 状态标签。
 *
 * 取值来自后端 `app/capability/executor.py`（WORKFLOW §4），
 * **不是** `SUCCEEDED` / `FAILED` —— 这两位在真实后端不存在，
 * 早期版本按它们映射导致所有运行都落到"等待中"。
 */
export function RunStateLabel({ status }: { status: string }) {
  return (
    <StatusLabel tone={RUN_TONE[status] ?? "default"}>{RUN_LABEL[status] ?? status}</StatusLabel>
  );
}

/** 运行终态：不可再取消。 */
export const RUN_FINAL_STATUSES = new Set(["APPROVED", "REJECTED", "CANCELLED"]);

const GATE_TONE: Record<string, StatusTone> = {
  approved: "success",
  pending: "warning",
  rejected: "danger",
  active: "info",
  planned: "default",
};

const GATE_LABEL: Record<string, string> = {
  approved: "已通过",
  pending: "待审核",
  rejected: "已驳回",
  active: "进行中",
  planned: "未开始",
};

/** 审核关口状态 → 原型状态标签。 */
export function GateStatus({ status }: { status: string }) {
  return (
    <StatusLabel tone={GATE_TONE[status] ?? "default"}>{GATE_LABEL[status] ?? status}</StatusLabel>
  );
}
